import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { SubmissionNoteControl } from './useSubmissionNote';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import type { AiJob, AiJobTrace } from '../../../lib/supabase.types';
import { aiJobModelLabel, CANCELABLE_SPEC_STATUSES } from '../../../lib/aiJobs';
import {
  aiJobChipLabel,
  aiJobKind,
  AI_JOB_STATUS_LABEL,
  aiJobStatusTone,
  formatClaimElapsed,
  isAiJobInProgress,
} from '../../../lib/aiJobLabels';
import { JOB_SOURCE_ICON, operatorClientName, type OperatorSummary, type OutlineHeading, PageSection, type SummaryChanges, type SummaryVersion } from '../../../lib/aiJobSummary';
import { ATTACHMENT_ACCEPT, jobAttachments } from '../../../lib/aiJobAttachments';
import { useTrelloCardUrl } from '../../../lib/trelloCardUrl';
import { tracesForKind } from '../../../lib/aiJobTraces';
import { PIECE_STATUS_LABEL, type ContentPiece } from '../../../lib/contentPieces';
import { Badge, Button, Icon, IconButton, SegmentedControl, Switch } from '../../ui';
import AiMarkdown from '../AiMarkdown';
import AiJobTraceView from '../AiJobTrace';
import { AttachmentLinks } from '../AttachmentDropzone';
import TrelloCardDetailBody from '../../comms/TrelloCardDetailBody';
import OperatorOverview from './OperatorOverview';
import StageStrip from './StageStrip';
import DecisionsDialog from './DecisionsDialog';
import SummaryVersions from './SummaryVersions';
import { LinkButton, OpCard } from './SectionHeading';
import { AddHandoffNoteButton, HandoffNoteCard, HandoffNoteField } from '../HandoffNote';
import { MAX_REVIEW_ROUNDS, specReviewBadge } from '../../../lib/specReview';

/**
 * Full-width job page for the operator. Slides in from the right and leaves
 * the Cockpit nav rail visible. Short sticky header (one title line with the
 * source chips, then the stage strip and submenu on one row), scrolling body,
 * and a sticky footer that starts collapsed: the actions, plus a one-line
 * "leave feedback" affordance that opens the reply box. State and handlers
 * stay with AiJobChips and arrive through `actions`.
 */

export type OperatorUploadTarget = 'note' | 'reply';

export interface OperatorPanelActions {
  reply: string;
  setReply: (value: string) => void;
  /** Optional note that rides with Approve spec / Approve build; sent with the click. */
  handoffDraft: string;
  setHandoffDraft: (value: string) => void;
  noteDraft: string;
  setNoteDraft: (value: string) => void;
  busy: boolean;
  error: string | null;
  uploading: OperatorUploadTarget | null;
  confirmCancel: boolean;
  setConfirmCancel: (v: boolean) => void;
  confirmCancelBuild: boolean;
  setConfirmCancelBuild: (v: boolean) => void;
  confirmDelete: boolean;
  setConfirmDelete: (v: boolean) => void;
  confirmSubmit: boolean;
  setConfirmSubmit: (v: boolean) => void;
  noteFileRef: React.RefObject<HTMLInputElement>;
  replyFileRef: React.RefObject<HTMLInputElement>;
  onPaste: (target: OperatorUploadTarget) => (e: React.ClipboardEvent) => void;
  onFilePick: (target: OperatorUploadTarget) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSaveNote: () => void;
  onToggleAutoApprove: (next: boolean) => void;
  onRevise: () => void;
  /** Approve to Build. Decision answers (id → answer) ride with the job for the builder. */
  onApproveSpec: (answers: Record<string, string>) => void;
  onCancel: () => void;
  onApproveBuild: () => void;
  onCancelBuild: () => void;
  onDelete: () => void;
  onSendBack: () => void;
  /** Complete / submitted → back to the build queue with the reply as the note. */
  onSendBackToBuild: () => void;
  onSubmitted: () => void;
  onViewPiece?: (piece: ContentPiece) => void;
  /** Files a parked item as its own Cockpit task. Absent on read-only renders. */
  onMakeTask?: (text: string, dueDate: string) => Promise<void>;
  /** Records a decision answer on the job, from the page. Absent on read-only renders. */
  onAnswerDecision?: (decisionId: string, answer: string | null) => Promise<void>;
  /** Saves an edited client note. Absent on read-only renders. */
  onSaveClientNote?: (markdown: string) => Promise<void>;
  /** Content jobs: saves an adjusted outline (null = the agent's). Absent on read-only renders. */
  onSaveOutline?: (headings: OutlineHeading[] | null, note: string | null) => Promise<void>;
  /** Page Build jobs: saves edited sections (null = the agent's). Absent on read-only renders. */
  onSaveSections?: (sections: PageSection[] | null) => Promise<void>;
  /** Page Build jobs: saves a changed target keyword (null = the agent's). Absent on read-only renders. */
  onSaveKeyword?: (keyword: { primary: string; secondary: string[] } | null) => Promise<void>;
  /** Client feedback jobs: the client note's Submit Note state. Absent on other jobs and read-only renders. */
  submissionNote?: SubmissionNoteControl;
}

export interface JobOperatorPanelProps {
  /** Design preview only: lets Page Build sections and Security Audit answers be edited on a read-only render (the save stays in the page). */
  sectionEdits?: boolean;
  job: AiJob;
  summary: OperatorSummary | null;
  traces: AiJobTrace[];
  piece: ContentPiece | null;
  actions: OperatorPanelActions;
  onClose: () => void;
  /** Every summary ever written for this job, oldest first. */
  versions?: SummaryVersion[];
  /** What changed in the current stage since its previous write. */
  changes?: SummaryChanges | null;
  /** Known client label when the job row has no joined task.client. */
  clientName?: string | null;
  /** Render as a normal block, no portal / overlay (design gallery). */
  inline?: boolean;
  /** Every action disabled; nothing fetches. */
  readOnly?: boolean;
}

type TabId = 'overview' | 'spec' | 'report' | 'approval' | 'versions' | 'trace' | 'card';

// Below the sidebar (z-30) so the nav rail can still expand over the page; above page content.
const OVERLAY_Z = 29;

const PHONE_QUERY = '(max-width: 767px)';

/** True below md, live as the window resizes or the phone rotates. */
function useIsPhone() {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(PHONE_QUERY);
    const sync = () => setPhone(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return phone;
}

export default function JobOperatorPanel({
  job,
  summary,
  traces,
  piece,
  actions,
  onClose,
  versions = [],
  changes = null,
  clientName,
  inline = false,
  readOnly = false,
  sectionEdits = false,
}: JobOperatorPanelProps) {
  const [tab, setTab] = useState<TabId>('overview');
  const isPhone = useIsPhone();
  const [decisionsOpen, setDecisionsOpen] = useState(false);
  const trelloUrl = useTrelloCardUrl(job.card_id);
  // Footer starts collapsed; a draft in the reply box keeps it open.
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // The go-ahead note sits behind "+ Add a note" next to Approve; a draft keeps it open.
  const [handoffOpen, setHandoffOpen] = useState(false);
  useEffect(() => {
    setTab('overview');
    setDecisionsOpen(false);
    setFeedbackOpen(false);
    setHandoffOpen(false);
  }, [job.id]);
  useEffect(() => {
    if (actions.reply.trim()) setFeedbackOpen(true);
  }, [actions.reply]);

  // Two-frame enter (see SidePanel): paint off-screen, then slide in.
  const [entered, setEntered] = useState(inline);
  useEffect(() => {
    if (inline) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [inline]);

  useEffect(() => {
    if (inline) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [inline, onClose]);

  // Navigating closes the page. Leaving the route unmounts us anyway; the case
  // this exists for is clicking the nav item for the route we are already on
  // (Agents while a job is open). React Router replaces the location with a
  // fresh key without remounting the page, so the panel would otherwise sit
  // there covering the screen the operator just asked to see.
  const location = useLocation();
  const seenLocationKey = useRef(location.key);
  useEffect(() => {
    if (inline) return;
    if (location.key === seenLocationKey.current) return;
    seenLocationKey.current = location.key;
    onClose();
  }, [inline, location.key, onClose]);

  const status = job.status;
  const kind = aiJobKind(status);
  const isUnwind = status.startsWith('unwind');
  const inProgress = isAiJobInProgress(status);
  const disabled = readOnly || actions.busy;

  const canApproveSpec = status === 'spec_ready';
  const canApproveBuild = status === 'build_ready';
  const canCancel = CANCELABLE_SPEC_STATUSES.includes(status);
  const canCancelBuild = status === 'build_ready';
  const canDelete = status === 'unwind_ready';
  const canSubmit = status === 'complete';
  const canSendBack = status === 'build_queued';
  // Security audits never auto-approve (the database refuses it too).
  const canAutoApprove = status === 'spec_queued' && job.task_type !== 'security_audit';
  const canEditNote = status === 'spec_queued' || status === 'build_queued';
  const replyMode: 'spec' | 'build' | 'rebuild' | null =
    status === 'spec_ready' ? 'spec' : status === 'build_ready' ? 'build' : status === 'complete' || status === 'submitted' ? 'rebuild' : null;

  const attachments = jobAttachments(job);
  const showReportTab = kind === 'build' || !!job.report_body;
  const showApprovalTab = !!job.approval_body || status === 'complete' || status === 'approval_in_progress' || status === 'approved';
  const showSpecTab = !!job.spec_body || kind === 'spec';
  const tabs = useMemo(() => {
    const list: { id: TabId; label: string }[] = [{ id: 'overview', label: 'Overview' }];
    if (showSpecTab) list.push({ id: 'spec', label: job.kind === 'content' ? 'Outline' : 'Spec' });
    if (showReportTab) list.push({ id: 'report', label: isUnwind ? 'Unwind report' : job.kind === 'content' ? 'Article report' : 'Build report' });
    if (showApprovalTab) list.push({ id: 'approval', label: 'Approval' });
    if (versions.length > 1) list.push({ id: 'versions', label: `Versions · ${versions.length}` });
    if (traces.length > 0) list.push({ id: 'trace', label: `Trace · ${traces.length}` });
    if (job.card_id) list.push({ id: 'card', label: 'Card' });
    return list;
  }, [showSpecTab, showReportTab, showApprovalTab, versions.length, traces.length, job.card_id, job.kind, isUnwind]);
  const activeTab = tabs.some((t) => t.id === tab) ? tab : 'overview';
  const decisions = summary?.spec?.decisions ?? [];
  // Answering on the page is real, so Approve only stops for what is still open.
  const unansweredDecisions = decisions.filter((d) => !d.answer?.trim());
  const isRebuild = status.startsWith('build') && !!job.approval_body;

  const client = clientName ?? operatorClientName(job);
  const title = summary?.spec?.title || job.title || aiJobChipLabel(job);
  const sources = summary?.spec?.sources ?? [];

  // Phones pin only the status line (with close); the title, chips, stage strip and tabs scroll away
  // with the body, which would otherwise get a sliver of a 667px screen between header and footer.
  const splitHeader = isPhone && !inline;

  const titleBlock = (
    <div className="min-w-0 flex-1 basis-full md:basis-0" style={{ display: 'flex', alignItems: 'center', gap: '6px 10px', flexWrap: 'wrap' }}>
      {client ? <span className="t-eyebrow" style={{ flexShrink: 0 }}>{client}</span> : null}
      <span
        className="md:truncate"
        style={{
          fontSize: 'var(--text-md)',
          fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
          letterSpacing: 'var(--tracking-tight)',
          lineHeight: 'var(--leading-tight)',
          color: 'var(--text-body)',
          minWidth: 0,
          maxWidth: '100%',
        }}
        title={title}
      >
        {title || AI_JOB_STATUS_LABEL[status]}
      </span>
      {/* Phones: the chips share one sideways-scrolling line instead of stacking one per row under the title. */}
      <span className="flex w-full gap-[6px] overflow-x-auto empty:hidden md:contents" style={{ scrollbarWidth: 'none' }}>
      {sources.map((s) =>
        s.url ? (
          <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" className="ck-op-chip pressable h-[32px] max-w-[85%] shrink-0 md:h-[24px] md:max-w-full md:shrink" title={s.label}>
            <Icon name={JOB_SOURCE_ICON[s.kind]} size={12} />
            <span style={{ maxWidth: '260px' }}>{s.label}</span>
            <Icon name="arrow-up-right" size={11} style={{ opacity: 0.6 }} />
          </a>
        ) : (
          <span key={s.id} className="ck-op-chip h-[32px] max-w-[85%] shrink-0 md:h-[24px] md:max-w-full md:shrink" title={s.label}>
            <Icon name={JOB_SOURCE_ICON[s.kind]} size={12} />
            <span style={{ maxWidth: '260px' }}>{s.label}</span>
          </span>
        )
      )}
      {sources.length === 0 && trelloUrl ? (
        <a href={trelloUrl} target="_blank" rel="noopener noreferrer" className="ck-op-chip pressable h-[32px] md:h-[24px]">
          <Icon name="clipboard-check" size={12} />
          <span>Trello card</span>
          <Icon name="arrow-up-right" size={11} style={{ opacity: 0.6 }} />
        </a>
      ) : null}
      </span>
    </div>
  );

  const review = specReviewBadge(job);

  const statusBlock = (
    <div className="order-first w-full justify-start md:order-none md:w-auto md:justify-end" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
      <span className="ck-badge-flip">
        <span key={status} className="ck-badge-flip-inner">
          <Badge tone={aiJobStatusTone(status)} dot={inProgress} pulse={inProgress}>
            {AI_JOB_STATUS_LABEL[status]}
          </Badge>
        </span>
      </span>
      {review ? (
        <Badge tone={review.tone} dot={review.pulse} pulse={review.pulse} title={review.title}>
          {review.label}
        </Badge>
      ) : null}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }} title="Model">
        <Icon name="sparkles" size={12} />
        {aiJobModelLabel(job.model)}
      </span>
      {inProgress && job.claimed_by ? (
        <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {job.claimed_by} · {formatClaimElapsed(job.claimed_at)}
        </span>
      ) : null}
      <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
        v{job.version}
      </span>
      {/* The auto margin pins close to the right on phones, where this line is full width; on desktop it is a no-op. */}
      {inline ? null : <IconButton icon="x" label="Close (Esc)" onClick={onClose} style={{ marginLeft: 'auto' }} />}
    </div>
  );

  const stageRow = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px 18px', flexWrap: 'wrap', marginTop: '8px' }}>
      <StageStrip job={job} />
      <SegmentedControl
        // nowrap labels: the control scrolls sideways on a phone instead of breaking "Build report" in two.
        options={tabs.map((t) => ({ id: t.id, label: <span style={{ whiteSpace: 'nowrap' }}>{t.label}</span> }))}
        value={activeTab}
        onChange={(id) => setTab(id as TabId)}
        style={{ flexShrink: 0, maxWidth: '100%', overflowX: 'auto' }}
      />
    </div>
  );

  const header = (
    <header
      style={{
        flexShrink: 0,
        background: 'var(--surface-card)',
        borderBottom: '1px solid var(--border-hairline)',
        boxShadow: inline ? 'none' : 'var(--shadow-rest)',
        zIndex: 2,
      }}
    >
      <div className="ck-op-col px-[16px] md:px-[28px]" style={{ paddingTop: splitHeader ? '6px' : '10px', paddingBottom: splitHeader ? '6px' : '10px' }}>
        {splitHeader ? (
          statusBlock
        ) : (
          <>
            {/* Row 1: one title line (client · task), the source chips, then status / model / version / close at the far right.
                Narrow: status line (with close) on top, then client, title (wrapping) and chips on the full width. */}
            <div className="flex-wrap md:flex-nowrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px 14px' }}>
              {titleBlock}
              {statusBlock}
            </div>
            {/* Row 2: stage strip and submenu on one line, centred on the circles. */}
            {stageRow}
          </>
        )}
      </div>
    </header>
  );

  const body = (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', background: 'var(--surface-canvas)' }}>
      {splitHeader ? (
        <div className="ck-op-col px-[16px]" style={{ paddingTop: '12px', paddingBottom: '12px', background: 'var(--surface-card)', borderBottom: '1px solid var(--border-hairline)' }}>
          {titleBlock}
          {stageRow}
        </div>
      ) : null}
      <div key={activeTab} className="ck-op-col ck-stagger px-[16px] pb-[32px] pt-[16px] md:px-[28px] md:pb-[48px] md:pt-[20px]" style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
        {activeTab === 'overview' ? (
          <>
            {attachments.length > 0 ? (
              <OpCard tone="raised" padding="10px 14px 8px">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <Icon name="paperclip" size={13} style={{ color: 'var(--text-faint)' }} />
                  <span className="t-eyebrow">Attachments · {attachments.length}</span>
                </div>
                <AttachmentLinks attachments={attachments} />
              </OpCard>
            ) : null}

            {job.kind === 'content' ? (
              piece ? (
                <OpCard style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', fontWeight: 500 }}>{piece.title || 'Untitled piece'}</div>
                    <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', marginTop: '2px' }}>
                      {PIECE_STATUS_LABEL[piece.status]} · v{piece.version} · preview, edit, mark image spots, download HTML
                    </div>
                  </div>
                  <Button icon="eye" disabled={readOnly} onClick={() => actions.onViewPiece?.(piece)} style={{ flexShrink: 0 }}>
                    View article
                  </Button>
                </OpCard>
              ) : kind === 'build' ? (
                <Hint icon="file-text" text="The article gets a View article button here once the writer hands it in." />
              ) : null
            ) : null}

            {canAutoApprove ? (
              <OpCard tone="raised" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>Auto Approve</div>
                  <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', marginTop: '2px' }}>
                    Skips spec review — jumps straight to the build queue when the spec is ready.
                  </div>
                </div>
                <Switch checked={job.auto_approve} disabled={disabled} onChange={actions.onToggleAutoApprove} />
              </OpCard>
            ) : null}

            {canEditNote ? (
              <div>
                <div className="t-eyebrow" style={{ marginBottom: '8px' }}>
                  Queue note
                </div>
                <textarea
                  value={actions.noteDraft}
                  onChange={(e) => actions.setNoteDraft(e.target.value)}
                  onPaste={actions.onPaste('note')}
                  placeholder="Context for the agent when it picks this up… (paste images or attach files)"
                  rows={3}
                  className="ck-textarea"
                  disabled={disabled}
                />
                <input ref={actions.noteFileRef} type="file" accept={ATTACHMENT_ACCEPT} multiple hidden onChange={actions.onFilePick('note')} />
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Button size="sm" variant="ghost" icon="paperclip" onClick={() => actions.noteFileRef.current?.click()} disabled={disabled || actions.uploading !== null}>
                    {actions.uploading === 'note' ? 'Uploading…' : 'Attach file'}
                  </Button>
                  {actions.noteDraft.trim() !== (job.queue_note ?? '').trim() ? (
                    <Button size="sm" variant="secondary" onClick={actions.onSaveNote} disabled={disabled || actions.uploading !== null}>
                      {actions.busy ? 'Saving…' : 'Save note'}
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {status === 'build_staged' ? (
              <Hint icon="lock" text={`${job.lock_reason || 'Waiting on another build lock.'} Agents cannot pick this up until those paths or that database are free.`} />
            ) : null}
            {status === 'approved' || status === 'approval_in_progress' ? (
              <Hint icon="rocket" text="An agent will merge, apply only unrecorded pending migrations, confirm the live deploy, then write an approval report. Path and database locks stay held until complete." />
            ) : null}
            {status === 'complete' ? (
              <Hint icon="check-circle-2" tone="up" text="Live. Read the approval, send the client note yourself, then click Submitted. That checks the comm chip. Path locks already dropped when this became complete." />
            ) : null}
            {isRebuild ? (
              <Hint icon="rotate-ccw" text="Rebuild. This job already went live once and was sent back. What's live stays live; the build agent starts from main again and opens a new PR. The earlier approval stays under the Approval tab." />
            ) : null}

            <HandoffNoteCard job={job} />

            {review && job.review_note && (job.review_state === 'passed' || job.review_state === 'stalled') ? (
              <OpCard tone="raised">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Icon name={job.review_state === 'passed' ? 'check-circle-2' : 'triangle-alert'} size={13} style={{ color: job.review_state === 'passed' ? 'var(--status-up)' : 'var(--status-warn)' }} />
                  <span className="t-eyebrow">
                    {job.review_state === 'passed'
                      ? `Opus review · passed on review ${job.review_round ?? 1} of ${MAX_REVIEW_ROUNDS} · answers picked, change any you disagree with`
                      : `Opus review · stalled after ${job.review_round ?? 0} of ${MAX_REVIEW_ROUNDS}`}
                  </span>
                </div>
                <AiMarkdown content={job.review_note} />
              </OpCard>
            ) : null}

            {job.revision_note ? (
              <OpCard tone="raised">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Icon name="rotate-ccw" size={13} style={{ color: 'var(--text-faint)' }} />
                  <span className="t-eyebrow">Last reply</span>
                </div>
                <AiMarkdown content={job.revision_note} />
              </OpCard>
            ) : null}

            <OperatorOverview
              job={job}
              summary={summary}
              readOnly={readOnly}
              changes={changes}
              onMakeTask={readOnly ? undefined : actions.onMakeTask}
              onAnswerDecision={readOnly && !sectionEdits ? undefined : actions.onAnswerDecision}
              onSaveClientNote={readOnly ? undefined : actions.onSaveClientNote}
              onSaveOutline={readOnly ? undefined : actions.onSaveOutline}
              onSaveSections={readOnly && !sectionEdits ? undefined : actions.onSaveSections}
              onSaveKeyword={readOnly && !sectionEdits ? undefined : actions.onSaveKeyword}
              submissionNote={readOnly ? undefined : actions.submissionNote}
            />
          </>
        ) : null}

        {activeTab === 'versions' ? <SummaryVersions versions={versions} /> : null}

        {activeTab === 'spec' ? (
          <MarkdownTab eyebrow={job.kind === 'content' ? 'Outline · as written' : 'Spec · as written'} content={job.spec_body} version={job.version} />
        ) : null}
        {activeTab === 'report' ? (
          <MarkdownTab eyebrow={`${isUnwind ? 'Unwind report' : 'Build report'} · as written`} content={job.report_body} version={job.version} />
        ) : null}
        {activeTab === 'approval' ? <MarkdownTab eyebrow="Approval report · as written" content={job.approval_body} version={job.version} /> : null}

        {activeTab === 'trace' ? (
          <>
            {(['approval', isUnwind ? 'unwind' : 'report', 'spec'] as const).map((k) => {
              const rows = tracesForKind(traces, k);
              if (rows.length === 0) return null;
              return (
                <OpCard key={k}>
                  <div className="t-eyebrow">{k === 'report' ? 'Build' : k[0].toUpperCase() + k.slice(1)} agent</div>
                  <AiJobTraceView traces={rows} />
                </OpCard>
              );
            })}
          </>
        ) : null}

        {activeTab === 'card' && job.card_id ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <span className="t-eyebrow">Trello card</span>
              {trelloUrl ? (
                <LinkButton href={trelloUrl} variant="secondary" icon="external-link">
                  Open in Trello
                </LinkButton>
              ) : null}
            </div>
            {readOnly ? (
              <EmptyState icon="clipboard-check" text="Card context loads from Trello on a live job." />
            ) : (
              <TrelloCardDetailBody cardId={job.card_id} />
            )}
          </>
        ) : null}
      </div>
    </div>
  );

  const confirmFooter = actions.confirmCancel ? (
    <>
      <Button onClick={actions.onCancel} disabled={disabled} variant="secondary">
        {actions.busy ? 'Cancelling…' : status === 'build_staged' ? 'Yes, cancel staged' : 'Yes, cancel spec'}
      </Button>
      <Button variant="ghost" disabled={disabled} onClick={() => actions.setConfirmCancel(false)}>
        Keep it
      </Button>
    </>
  ) : actions.confirmCancelBuild ? (
    <>
      <Button onClick={actions.onCancelBuild} disabled={disabled} variant="secondary">
        {actions.busy ? 'Queuing unwind…' : 'Yes, send to unwind'}
      </Button>
      <Button variant="ghost" disabled={disabled} onClick={() => actions.setConfirmCancelBuild(false)}>
        Keep it
      </Button>
    </>
  ) : actions.confirmDelete ? (
    <>
      <Button onClick={actions.onDelete} disabled={disabled} variant="secondary">
        {actions.busy ? 'Deleting…' : 'Yes, delete job'}
      </Button>
      <Button variant="ghost" disabled={disabled} onClick={() => actions.setConfirmDelete(false)}>
        Keep it
      </Button>
    </>
  ) : actions.confirmSubmit ? (
    <>
      <Button onClick={actions.onSubmitted} disabled={disabled} icon="check">
        {actions.busy ? 'Submitting…' : 'Yes, mark submitted'}
      </Button>
      <Button variant="ghost" disabled={disabled} onClick={() => actions.setConfirmSubmit(false)}>
        Keep it
      </Button>
    </>
  ) : null;

  const showHandoff = (canApproveSpec || canApproveBuild) && !confirmFooter;
  // A draft keeps the field open. Derived, not set from an effect: setting it on every keystroke tripped
  // React's nested-update limit when text arrived fast (pasted or scripted).
  const handoffShownOpen = handoffOpen || Boolean(actions.handoffDraft.trim());
  const handoffFieldShown = showHandoff && handoffShownOpen;

  const hasActions = canApproveSpec || canApproveBuild || canSubmit || canCancel || canSendBack || canCancelBuild || canDelete;

  const actionButtons = confirmFooter ?? (
    <>
      {canCancel ? (
        <Button variant="ghost" disabled={disabled} onClick={() => actions.setConfirmCancel(true)}>
          {status === 'build_staged' ? 'Cancel staged' : 'Cancel spec'}
        </Button>
      ) : null}
      {canSendBack ? (
        <Button variant="ghost" disabled={disabled} onClick={actions.onSendBack}>
          {actions.busy ? 'Sending back…' : 'Send back'}
        </Button>
      ) : null}
      {canCancelBuild ? (
        <Button variant="ghost" disabled={disabled} onClick={() => actions.setConfirmCancelBuild(true)}>
          Cancel build
        </Button>
      ) : null}
      {canDelete ? (
        <Button variant="ghost" disabled={disabled} onClick={() => actions.setConfirmDelete(true)}>
          Delete
        </Button>
      ) : null}
      {showHandoff && !handoffShownOpen ? <AddHandoffNoteButton disabled={disabled} onClick={() => setHandoffOpen(true)} /> : null}
      {canApproveSpec ? (
        <Button
          onClick={() => (unansweredDecisions.length > 0 ? setDecisionsOpen(true) : actions.onApproveSpec({}))}
          disabled={disabled}
          icon={unansweredDecisions.length > 0 ? 'help-circle' : 'check'}
          title={
            unansweredDecisions.length > 0
              ? `Still unanswered: ${unansweredDecisions.map((d) => d.id).join(', ')}`
              : undefined
          }
        >
          {unansweredDecisions.length > 0
            ? `Approve to Build · ${unansweredDecisions.length} ${unansweredDecisions.length === 1 ? 'call' : 'calls'}`
            : 'Approve to Build'}
        </Button>
      ) : null}
      {canApproveBuild ? (
        <Button onClick={actions.onApproveBuild} disabled={disabled} icon="check">
          Approve build
        </Button>
      ) : null}
      {canSubmit ? (
        <Button onClick={() => actions.setConfirmSubmit(true)} disabled={disabled} icon="check">
          Submitted
        </Button>
      ) : null}
    </>
  );

  const replyLabel = replyMode === 'spec' ? 'Revise spec' : replyMode === 'build' ? 'Revise build' : 'Send back to build';
  const replyInvite =
    replyMode === 'spec'
      ? 'Something off in the spec? Tell the agent what to change'
      : replyMode === 'build'
        ? 'Something off in the build? Tell the agent what to change'
        : 'Caught something live? Send it back to build';
  const replySendDisabled = disabled || actions.uploading !== null || !actions.reply.trim();
  const onReplySend = replyMode === 'rebuild' ? actions.onSendBackToBuild : actions.onRevise;

  const footer =
    replyMode || hasActions || actions.error ? (
      <footer
        style={{
          flexShrink: 0,
          background: 'var(--surface-card)',
          borderTop: '1px solid var(--border-hairline)',
          boxShadow: inline ? 'none' : '0 -8px 24px rgba(0, 0, 0, 0.12)',
          zIndex: 2,
        }}
      >
        <div
          className="ck-op-col px-[16px] md:px-[28px]"
          style={{ paddingTop: feedbackOpen && replyMode ? '12px' : '10px', paddingBottom: feedbackOpen && replyMode ? '14px' : '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}
        >
          {actions.error ? (
            <div
              role="alert"
              style={{
                padding: '8px 12px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--status-down-soft)',
                border: '1px solid rgba(229, 72, 77, 0.25)',
                color: 'var(--danger-400)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {actions.error}
            </div>
          ) : null}
          <div className="ck-op-footer-grid" style={(feedbackOpen && replyMode) || handoffFieldShown ? undefined : { alignItems: 'center' }}>
            {replyMode && !feedbackOpen ? (
              <div style={{ minWidth: 0 }}>
                {/* Phones: the invite wraps to two lines, so the fixed 32px pill grows with it into a full-width target. */}
                <button
                  type="button"
                  className="ck-op-feedback-toggle pressable max-md:h-auto max-md:min-h-[40px] max-md:w-full max-md:py-[8px] max-md:text-left max-md:leading-snug"
                  disabled={readOnly}
                  onClick={() => setFeedbackOpen(true)}
                >
                  <Icon name="message-square" size={14} />
                  <span>{replyInvite}</span>
                  <Icon name="chevron-right" size={13} style={{ opacity: 0.6 }} />
                </button>
              </div>
            ) : replyMode ? (
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px 10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                  <span className="t-eyebrow">{replyLabel}</span>
                  {replyMode === 'rebuild' ? (
                    <span className="max-md:order-last max-md:basis-full" style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>reopens the build queue · what's live stays live until the rebuild lands</span>
                  ) : null}
                  <span style={{ flex: 1 }} />
                  <Button size="sm" variant="ghost" icon="chevron-down" onClick={() => setFeedbackOpen(false)} disabled={actions.busy}>
                    Collapse
                  </Button>
                </div>
                <textarea
                  value={actions.reply}
                  onChange={(e) => actions.setReply(e.target.value)}
                  onPaste={actions.onPaste('reply')}
                  placeholder={
                    replyMode === 'rebuild'
                      ? 'What should the rebuild change? (paste images or attach files)'
                      : 'What should change? (paste images or attach files)'
                  }
                  rows={3}
                  className="ck-textarea"
                  disabled={disabled}
                  style={{ resize: 'vertical', minHeight: '64px', maxHeight: '40dvh' }}
                />
                <input ref={actions.replyFileRef} type="file" accept={ATTACHMENT_ACCEPT} multiple hidden onChange={actions.onFilePick('reply')} />
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Button size="sm" variant="ghost" icon="paperclip" onClick={() => actions.replyFileRef.current?.click()} disabled={disabled || actions.uploading !== null}>
                    {actions.uploading === 'reply' ? 'Uploading…' : 'Attach file'}
                  </Button>
                  <Button size="sm" variant="secondary" icon={replyMode === 'rebuild' ? 'rotate-ccw' : 'send'} onClick={onReplySend} disabled={replySendDisabled}>
                    {actions.busy ? 'Sending…' : replyMode === 'rebuild' ? 'Send back to build' : 'Send'}
                  </Button>
                </div>
              </div>
            ) : (
              <div />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px', minWidth: 0 }}>
              {handoffFieldShown ? (
                <HandoffNoteField
                  value={actions.handoffDraft}
                  onChange={actions.setHandoffDraft}
                  disabled={disabled}
                  autoFocus={!actions.handoffDraft}
                  onRemove={() => {
                    actions.setHandoffDraft('');
                    setHandoffOpen(false);
                  }}
                  style={{ width: '440px', maxWidth: '100%' }}
                />
              ) : null}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>{actionButtons}</div>
            </div>
          </div>
        </div>
      </footer>
    ) : null;

  const decisionsDialog =
    decisionsOpen && unansweredDecisions.length > 0 ? (
      <DecisionsDialog
        decisions={unansweredDecisions}
        busy={actions.busy}
        onCancel={() => setDecisionsOpen(false)}
        onConfirm={(answers) => {
          setDecisionsOpen(false);
          actions.onApproveSpec(answers);
        }}
      />
    ) : null;

  if (inline) {
    return (
      <div
        className="ck-op-root is-inline"
        style={{ position: 'relative', display: 'flex', flexDirection: 'column', background: 'var(--surface-canvas)', minHeight: 0, height: '100%' }}
      >
        {header}
        {body}
        {footer}
        {decisionsDialog}
      </div>
    );
  }

  return createPortal(
    <div className="ck-overlay-root">
      <section
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`ck-op-root ck-op-overlay${entered ? ' is-shown' : ''}`}
        style={{
          zIndex: OVERLAY_Z,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface-canvas)',
          boxShadow: 'var(--shadow-pop)',
          borderLeft: '1px solid var(--border-hairline)',
          fontFamily: 'var(--font-ui)',
        }}
      >
        {header}
        {body}
        {footer}
        {decisionsDialog}
      </section>
    </div>,
    document.body
  );
}

function Hint({ icon, text, tone = 'neutral' }: { icon: string; text: string; tone?: 'neutral' | 'up' }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        padding: '10px 14px',
        borderRadius: 'var(--radius-md)',
        background: tone === 'up' ? 'var(--status-up-soft)' : 'var(--surface-raised)',
        border: '1px solid var(--border-hairline)',
        fontSize: 'var(--text-sm)',
        lineHeight: 'var(--leading-snug)',
        color: tone === 'up' ? 'var(--text-body)' : 'var(--text-muted)',
      }}
    >
      <Icon name={icon} size={14} style={{ color: tone === 'up' ? 'var(--status-up)' : 'var(--text-faint)', flexShrink: 0, marginTop: '2px' }} />
      <span>{text}</span>
    </div>
  );
}

function MarkdownTab({ eyebrow, content, version }: { eyebrow: string; content: string; version: number }) {
  return (
    <OpCard padding="18px 20px">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px', marginBottom: '12px' }}>
        <span className="t-eyebrow">{eyebrow}</span>
        <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          v{version} · {content.length.toLocaleString()} chars
        </span>
      </div>
      {content.trim() ? <AiMarkdown content={content} /> : <EmptyState icon="file-text" text="Nothing written yet." />}
    </OpCard>
  );
}

function EmptyState({ icon, text }: { icon: string; text: string }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        padding: '28px 16px',
        border: '1px dashed var(--border-strong)',
        borderRadius: 'var(--radius-md)',
        color: 'var(--text-faint)',
        fontSize: 'var(--text-sm)',
        textAlign: 'center',
      }}
    >
      <Icon name={icon} size={18} />
      <span>{text}</span>
    </div>
  );
}
